import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { newApplicationStateService, type NewApplicationState } from '../services/new-application-state.service';

const queryKey = ['new-application-state'];

const getNewApplicationStateQuery = (queryClient: QueryClient) =>
  queryClient.ensureQueryData<NewApplicationState>({
    queryKey,
    queryFn: newApplicationStateService.getState,
    staleTime: Infinity,
    gcTime: Infinity,
  });

const invalidateNewApplicationStateQuery = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey });

const useNewApplicationStateQuery = () => {
  return useQuery({
    queryKey,
    queryFn: newApplicationStateService.getState,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

const useNewApplicationStateMutation = () => {
  const queryClient = useQueryClient();

  const previousState = useRef<NewApplicationState | undefined>(undefined);

  return useMutation<NewApplicationState, Error, Partial<NewApplicationState>>({
    mutationFn: value => {
      return newApplicationStateService.setState(value);
    },
    onSuccess: value => {
      invalidateNewApplicationStateQuery(queryClient);
      previousState.current = value;
    },
  });
};

export const useNewApplicationState = {
  get: getNewApplicationStateQuery,
  invalidate: invalidateNewApplicationStateQuery,
  query: useNewApplicationStateQuery,
  mutation: useNewApplicationStateMutation,
};
